import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location, LocationGeneratorData } from '../location/location';
import { LocationType } from '../location/location-type';
import { LocationService } from '../location/location.service';
import { LocationTypeService } from '../location/location-type.service';
import { Area, AreaBarcodeFormat } from '../location/area';
import { AreaService } from '../location/area.service';
import { LocationLogicService } from '../location/location-logic.service';

import { Message, ConfirmationService, SelectItem, MenuItem } from 'primeng/primeng';

@Component({
  selector: 'app-location',
  templateUrl: './location-generator.component.html',
  styleUrls: ['./location-generator.component.css'],
  providers: [ConfirmationService]
})
export class LocationGeneratorComponent implements OnInit {

  locationList: Location[];
  msgs: Message[];
  area: Area;
  locationTypeList: SelectItem[];
  generatorSteps: MenuItem[] = [{ label: "Regal" }, { label: "Zonen" }, { label: "Plätze" }, { label: "Generieren" }];
  activeStep: number = 0;
  generatorDataSets: LocationGeneratorData[];

  /**
   * Page form data
   */
  // Rack addition page
  formRackInputRack: string;

  // Zone selection
  zoneSelectItems: SelectItem[] = [{ label: "1-Zone", value: 1 }, { label: "2-Zonen", value: 2 }, { label: "3-Zonen", value: 3 }];

  // Generation progress page
  zoneGenerationProgress: number = 0;
  stepSizeProgress: number = 1;
  zoneGenerationActiveIndex: number = 0;

  /** 
   * Wizard settings, possible parts of a barcode are contained area barcodeformat.
   */
  barcodeFormat: AreaBarcodeFormat = new AreaBarcodeFormat();

  /**
   *  Constructor of component
   * @param locationService 
   * @param router 
   * @param confirmationService 
   * @param areaService 
   * @param route 
   * @param locationTypeService 
   * @param locatLogic
   */
  constructor(private locationService: LocationService, private router: Router,
    private confirmationService: ConfirmationService,
    private areaService: AreaService, private route: ActivatedRoute,
    private locationTypeService: LocationTypeService,
    private locatLogic: LocationLogicService) {
    this.locationList = [];
    this.msgs = [];
    this.area = new Area();
    this.generatorDataSets = [];
  }

  ngOnInit() {

    if (this.route.snapshot.params['id'] != null) {
      this.areaService.qryArea(+this.route.snapshot.params['id']).subscribe(
        (area: Area) => {
          if (area != null) {
            this.area = area;
            this.barcodeFormat = this.locatLogic.parseBarcodeElements(this.area.barcodeformat);
            this.testIfRackIsInBarcode();
          } else {
            this.msgs.push({ severity: 'warn', summary: 'Failed to load location.' });
          }
        },
        (err) => {
          this.msgs = [];
          this.msgs.push({ severity: 'warn', summary: 'Failed to load location.' })
        }
      );
    }

    this.loadLocationTypes();
  }

  private loadLocationTypes() {
    this.locationTypeList = [];
    this.locationTypeService.list().subscribe(
      (locationTypeArray: LocationType[]) => {
        this.locationTypeList = [];
        for (var locationType of locationTypeArray)
          this.locationTypeList.push({ label: locationType.name, value: locationType });
      },
      (err) => {
        console.log(err);
        this.msgs = [];
        this.msgs.push({ severity: 'warn', summary: 'Error', detail: 'Could not load list of locationTypes.' });
      }
    );
  }

  /**
   * Checks if rack is required in barcode. If not
   * we move on to zone selection directly and create a 
   * dummy rack.
   */
  private testIfRackIsInBarcode(): void {
    if (!this.barcodeFormat.rack) {
      let rack: LocationGeneratorData = new LocationGeneratorData();
      rack.area = this.area;
      rack.rack = 0 + "";
      rack.numberOfZones = 1;
      this.generatorDataSets.push(rack);

      // Jump to zone selection:
      this.activeStep = 1;
    }
  }

  /**
   * Form input handler
   */
  // Rack addition form
  /**
   * Adds rack to list of generator datasets
   */
  public formRackAddRack(): void {
    if (this.formRackInputRack != null && this.formRackInputRack.length > 0) {
      if (this.formRackInputRack.length > this.barcodeFormat.l_rack) {
        this.msgs = [];
        this.msgs.push({ severity: 'info', summary: 'Die Regalnummer ist länger als im Format erlaubt. Länge: ' + this.barcodeFormat.l_area });
        return;
      }

      // is rack already in list?
      for (var r of this.generatorDataSets) {
        if (r.rack == this.formRackInputRack) {
          this.msgs = [];
          this.msgs.push({ severity: 'info', summary: 'Das Regal befindet sich bereits in der Liste.' });
          return;
        }
      }

      let rack: LocationGeneratorData = new LocationGeneratorData();
      rack.area = this.area;
      rack.rack = this.locatLogic.formatRack(this.formRackInputRack, this.barcodeFormat);
      rack.numberOfZones = 1;
      this.generatorDataSets.push(rack);
      this.formRackInputRack = "";
    } else {
      this.msgs = [];
      this.msgs.push({ severity: 'info', summary: 'Bitte erst eine Regalnummer eingeben.' });
    }
  }

  /**
   * Removes a rack from generation area list
   * @param pRack 
   */
  public formRackRemoveRack(pRack: LocationGeneratorData): void {
    let tmpRackList: LocationGeneratorData[] = [];
    for (var r of this.generatorDataSets) {
      if (r.rack != pRack.rack) {
        tmpRackList.push(r);
      }
    }
    this.generatorDataSets = tmpRackList;
  }

  /**
   * Navigates to next step in wizard if at least one zone was added
   */
  public formRackNext(): void {
    if (this.generatorDataSets != null && this.generatorDataSets.length > 0) {
      this.activeStep = 1;
    } else {
      this.msgs = [];
      this.msgs.push({ severity: 'info', summary: 'Bitte erst eine Regalnummer eingeben.' });
    }
  }

  // Page zone selection
  /**
   * Navigates to next step after number of zones in each rack was selected.
   * Duplicates zones according to number of zones in rack.
   */
  public formZoneNext(): void {
    let tmpRackList: LocationGeneratorData[] = [];
    for (var r of this.generatorDataSets) {
      for (var i = 0; i < r.numberOfZones; i++) {
        let tmpR: LocationGeneratorData = new LocationGeneratorData();
        tmpR.area = this.area;
        tmpR.userid = r.userid;
        tmpR.rack = r.rack;
        tmpR.numberOfZones = i + 1;

        // set default values
        tmpR.tray_start = 0;
        tmpR.tray_step = 10;
        if (this.barcodeFormat.tray)
          tmpR.tray_end = 10;
        else
          tmpR.tray_end = 0

        tmpR.level_start = 0;
        tmpR.level_step = 10;
        if (this.barcodeFormat.level)
          tmpR.level_end = 10;
        else
          tmpR.level_end = 0;

        tmpR.capacityMax = 1;
        tmpR.loadMax = 99;

        tmpRackList.push(tmpR);
      }
    }

    this.generatorDataSets = tmpRackList;

    this.activeStep = 2;
  }

  /**
   * Goes back to rack specification page
   */
  public formZoneBack(): void {
    this.activeStep = 0;
  }

  // Rack page
  /**
   * Shows last page of wizard and starts generation of 
   * locations. For each rack an api call is done to
   * start generating set of locations with given type.
   */
  public formStartGeneration(): void {
    // First check if one value is not set
    for (var r of this.generatorDataSets) {
      if (r.locationType == null) {
        this.msgs.push({ severity: 'warn', summary: 'Bei allen Zonen muss ein Typ gesetzt werden.' });
        return;
      }
      if (!(r.capacityMax > 0 && r.loadMax > 0)) {
        this.msgs.push({ severity: 'warn', summary: 'Bei allen Zonen muss die Kapazität und die Tragkraft gesetzt werden.' });
        return;
      }
      if (!((r.level_end+"").length > 0 && r.level_end >= 0 && (r.level_start+"").length > 0 
        && r.level_start >= 0 && (r.level_step+"").length > 0 && r.level_step >= 0)) {
        this.msgs.push({ severity: 'warn', summary: 'Bei allen Zonen müssen die Ebenebereiche gesetzt werden.' });
        return;
      }
      if (!((r.tray_end+"").length > 0 && r.tray_end >= 0 && (r.tray_start+"").length > 0 
        && r.tray_start >= 0 && (r.tray_step+"").length > 0 && r.tray_step >= 0)) {
        this.msgs.push({ severity: 'warn', summary: 'Bei allen Zonen müssen die Ebenebereiche gesetzt werden.' });
        return;
      }
    }

    // Ok, everything is fine so let's trigger generation process
    this.stepSizeProgress = 100 / this.generatorDataSets.length;
    this.activeStep = 3;
    this.callGenerationServiceForEachDataset();
  }

  // Generation page
  public formBackToOverview(): void {
    this.router.navigate(["area", "locations", this.area.id]);
  }

  public callGenerationServiceForEachDataset(): void {
    this.locationService.generateSetOfLocations(
      this.generatorDataSets[this.zoneGenerationActiveIndex]).subscribe(
      (success: any) => {
        if (success.success) {
          this.msgs.push({ severity: 'info', summary: 'Hinzugefügt: Regal: ' + success.rack + ' Zone: ' + success.zone + ' Anzahl: ' + success.count });
        } else {
          this.msgs.push({ severity: 'warn', summary: 'Platz konnte nicht hinzugefügt werden: Regal: ' + success.rack + ' Zone: ' + success.zone });
        }
        this.checkForNextApiCallOrEnd();
      },
      (err) => {
        console.log(err);
        this.msgs = [];
        this.msgs.push({ severity: 'warn', summary: 'Error', detail: 'Could not create set of locations.' });
      }
      );
  }

  /**
   * Checks if we have finished all api calls.
   * If not next call is started.
   */
  public checkForNextApiCallOrEnd(): void {
    this.zoneGenerationProgress = this.zoneGenerationProgress + this.stepSizeProgress;
    this.zoneGenerationActiveIndex = this.zoneGenerationActiveIndex + 1;
    if (this.zoneGenerationActiveIndex < this.generatorDataSets.length) {
      this.callGenerationServiceForEachDataset();
      return;
    }
    // last we have to set progress to 100 for sure to show
    // finish button
    this.zoneGenerationProgress = 100;
  }
}



// WEBPACK FOOTER //
// /home/pneuser/workspace/pepelogistic/pepeLogisticControl/src/app/location-generator/location-generator.component.ts